/**
 * Onboarding Flows - Admin page
 * Tabs:
 * 1. Fluxos (CRUD + templates + activate/deactivate)
 * 2. Leads (list with funnel summary + filters + pagination)
 */

import { useState, useEffect, useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import {
  Card, CardContent,
  Button, useToast, ConfirmDialog,
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@payflow/ui";
import FlowEditor from "../../components/FlowEditor";

const API_BASE = (import.meta as any).env?.VITE_API_URL || "http://localhost:8787";
const TOKEN_KEY = "payflow_token";

// ============ Types ============

type OnboardingStepType =
  | "cpf_lookup" | "otp_whatsapp" | "cep_lookup" | "text_input"
  | "multiple_choice" | "multi_select" | "document_upload" | "selfie"
  | "video_recording" | "date_input" | "currency_input" | "number_input"
  | "info_screen" | "loan_simulator" | "create_password"
  // Tipos legados vindos do DB em fluxos antigos (ED). FlowEditor
  // renderiza esses como step generico, mas precisamos aceitar no tipo.
  | "phone_input" | "select" | "radio" | "file_upload"
  | "references" | "account_link" | "terms" | "penalty_warning"
  | "authorizations" | "pix_input" | "video_upload"
  | string;

type OnboardingOption = string | { label?: string; value?: string; icon?: string };

interface OnboardingStepCondition {
  field?: string;
  equals?: string;
  not_equals?: string;
  in?: string[];
  not_in?: string[];
  not_empty?: boolean;
  and?: OnboardingStepCondition[];
  or?: OnboardingStepCondition[];
}

interface OnboardingStepConfig {
  label: string;
  field?: string;
  placeholder?: string;
  message?: string;
  options?: OnboardingOption[];
  accept?: string[];
  maxSizeMB?: number;
  minLength?: number;
  maxLength?: number;
  regex?: string;
  min?: number;
  max?: number;
  camera?: "front" | "back";
  maxDurationSeconds?: number;
}

interface OnboardingStep {
  order: number;
  type: OnboardingStepType;
  required: boolean;
  condition: OnboardingStepCondition | null;
  config: OnboardingStepConfig;
}

interface OnboardingFlow {
  id: string;
  tenantId: string;
  nome: string;
  descricao: string | null;
  isActive: boolean;
  steps: OnboardingStep[];
  stepsCount: number;
  createdAt: string;
  updatedAt: string;
}

interface OnboardingLead {
  id: string;
  cpf: string | null;
  nome: string | null;
  whatsapp: string | null;
  channel: string;
  currentStep: number;
  totalSteps: number;
  status: string;
  startedAt: string;
  lastActivityAt: string;
  completedAt: string | null;
}

interface FunnelData {
  totalStarted: number;
  totalCompleted: number;
  conversionRate: number;
  steps: { order: number; label: string; count: number }[];
}

// ============ Helpers ============

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch {
    return iso;
  }
}

function statusBadge(status: string) {
  switch (status) {
    case "completed":
      return { bg: "bg-green-100 dark:bg-green-900/30", text: "text-green-700 dark:text-green-400", label: "Completo" };
    case "in_progress":
      return { bg: "bg-amber-100 dark:bg-amber-900/30", text: "text-amber-700 dark:text-amber-400", label: "Em andamento" };
    case "abandoned":
      return { bg: "bg-red-100 dark:bg-red-900/30", text: "text-red-700 dark:text-red-400", label: "Abandonado" };
    default:
      return { bg: "bg-gray-100 dark:bg-gray-800", text: "text-gray-600 dark:text-gray-400", label: status };
  }
}

function channelLabel(ch: string): string {
  if (ch === "app") return "App";
  if (ch === "whatsapp_bot") return "WhatsApp Bot";
  return ch;
}

// ============ Icon Component ============

function HeroIcon({ d, className = "w-5 h-5" }: { d: string; className?: string }) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className={className}>
      <path strokeLinecap="round" strokeLinejoin="round" d={d} />
    </svg>
  );
}

// ============ Tabs ============

type TabId = "fluxos" | "leads";

const TABS: { id: TabId; label: string }[] = [
  { id: "fluxos", label: "Fluxos" },
  { id: "leads", label: "Leads" },
];

// ============ Main Page Component ============

export function OnboardingPage() {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [tab, setTab] = useState<TabId>("fluxos");

  // Flows state
  const [flows, setFlows] = useState<OnboardingFlow[]>([]);
  const [flowsLoading, setFlowsLoading] = useState(true);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editingFlow, setEditingFlow] = useState<OnboardingFlow | null>(null);
  const [flowForm, setFlowForm] = useState<{ nome: string; descricao: string; steps: OnboardingStep[] }>({
    nome: "", descricao: "", steps: [],
  });
  // Snapshot inicial do flowForm pro prompt de alteracoes nao salvas.
  const flowInitialRef = useRef<typeof flowForm | null>(null);
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<OnboardingFlow | null>(null);
  // Old linear editor state removed - now using FlowEditor component

  // Leads state
  const [leads, setLeads] = useState<OnboardingLead[]>([]);
  const [leadsLoading, setLeadsLoading] = useState(false);
  const [leadsMeta, setLeadsMeta] = useState({ page: 1, limit: 20, total: 0, totalPages: 1 });
  const [leadsPage, setLeadsPage] = useState(1);
  const [leadsStatusFilter, setLeadsStatusFilter] = useState("");
  const [leadsChannelFilter, setLeadsChannelFilter] = useState("");
  const [funnel, setFunnel] = useState<FunnelData | null>(null);
  const [funnelFlowId, setFunnelFlowId] = useState("");

  const getHeaders = () => ({
    "Content-Type": "application/json",
    Authorization: `Bearer ${localStorage.getItem(TOKEN_KEY)}`,
  });

  // ============ Fetch Flows ============

  const fetchFlows = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/v1/admin/onboarding/flows`, {
        headers: getHeaders(),
      });
      const json = await res.json();
      if (json.success && json.data) {
        setFlows(json.data);
      }
    } catch {
      toast({ variant: "error", title: "Erro ao carregar fluxos" });
    } finally {
      setFlowsLoading(false);
    }
  }, []);

  // ============ Fetch Leads ============

  const fetchLeads = useCallback(async (page = 1) => {
    setLeadsLoading(true);
    try {
      const params = new URLSearchParams({ page: String(page), limit: String(leadsMeta.limit) });
      if (leadsStatusFilter) params.set("status", leadsStatusFilter);
      if (leadsChannelFilter) params.set("channel", leadsChannelFilter);
      const res = await fetch(`${API_BASE}/v1/admin/onboarding/leads?${params}`, {
        headers: getHeaders(),
      });
      const json = await res.json();
      if (json.success) {
        setLeads(json.data || []);
        if (json.meta) setLeadsMeta(json.meta);
      }
    } catch {
      toast({ variant: "error", title: "Erro ao carregar leads" });
    } finally {
      setLeadsLoading(false);
    }
  }, [leadsStatusFilter, leadsChannelFilter, leadsMeta.limit]);

  // ============ Fetch Funnel ============

  const fetchFunnel = useCallback(async (flowId: string) => {
    if (!flowId) { setFunnel(null); return; }
    try {
      const res = await fetch(`${API_BASE}/v1/admin/onboarding/leads/funnel?flowId=${flowId}`, {
        headers: getHeaders(),
      });
      const json = await res.json();
      if (json.success && json.data) {
        setFunnel(json.data);
      }
    } catch {
      // silent
    }
  }, []);

  // ============ Effects ============

  useEffect(() => {
    fetchFlows();
  }, [fetchFlows]);

  useEffect(() => {
    if (tab === "leads") {
      fetchLeads(leadsPage);
    }
  }, [tab, leadsPage, leadsStatusFilter, leadsChannelFilter]);

  useEffect(() => {
    if (tab === "leads" && funnelFlowId) {
      fetchFunnel(funnelFlowId);
    }
  }, [tab, funnelFlowId]);

  // Set default funnel flow when flows load
  useEffect(() => {
    if (flows.length > 0 && !funnelFlowId) {
      const active = flows.find((f) => f.isActive);
      setFunnelFlowId(active?.id || flows[0].id);
    }
  }, [flows, funnelFlowId]);

  // ============ Save Flow ============

  const handleSaveFlow = async () => {
    if (!flowForm.nome.trim()) {
      toast({ variant: "warning", title: t("onboarding_admin.validate_name") });
      return;
    }
    if (flowForm.steps.length === 0) {
      toast({ variant: "warning", title: t("onboarding_admin.validate_step") });
      return;
    }
    for (const s of flowForm.steps) {
      if (!s.config.label.trim()) {
        toast({ variant: "warning", title: t("onboarding_admin.validate_step_label", { order: s.order }) });
        return;
      }
    }

    setSaving(true);
    try {
      const body = {
        nome: flowForm.nome.trim(),
        descricao: flowForm.descricao.trim() || null,
        steps: flowForm.steps.map((s, i) => ({ ...s, order: i + 1 })),
      };
      const url = editingFlow
        ? `${API_BASE}/v1/admin/onboarding/flows/${editingFlow.id}`
        : `${API_BASE}/v1/admin/onboarding/flows`;
      const res = await fetch(url, {
        method: editingFlow ? "PUT" : "POST",
        headers: getHeaders(),
        body: JSON.stringify(body),
      });
      const json = await res.json();
      if (json.success) {
        toast({ variant: "success", title: editingFlow ? t("onboarding_admin.flow_updated") : t("onboarding_admin.flow_created") });
        setEditorOpen(false);
        setEditingFlow(null);
        fetchFlows();
      } else {
        toast({ variant: "error", title: json.error?.message || t("onboarding_admin.save_error") });
      }
    } catch {
      toast({ variant: "error", title: t("common.connection_error") });
    } finally {
      setSaving(false);
    }
  };

  // ============ Delete Flow ============

  const handleDeleteFlow = async () => {
    if (!deleteTarget) return;
    try {
      const res = await fetch(`${API_BASE}/v1/admin/onboarding/flows/${deleteTarget.id}`, {
        method: "DELETE",
        headers: getHeaders(),
      });
      const json = await res.json();
      if (json.success) {
        toast({ variant: "success", title: t("onboarding_admin.flow_deleted") });
        fetchFlows();
      } else {
        toast({ variant: "error", title: json.error?.message || t("onboarding_admin.delete_error") });
      }
    } catch {
      toast({ variant: "error", title: t("common.connection_error") });
    } finally {
      setDeleteTarget(null);
    }
  };

  // ============ Activate / Deactivate ============

  const handleToggleActive = async (flow: OnboardingFlow) => {
    try {
      if (flow.isActive) {
        // Deactivate = update isActive to false via PUT
        const res = await fetch(`${API_BASE}/v1/admin/onboarding/flows/${flow.id}`, {
          method: "PUT",
          headers: getHeaders(),
          body: JSON.stringify({ nome: flow.nome, steps: flow.steps }),
        });
        const json = await res.json();
        if (json.success) {
          toast({ variant: "success", title: t("onboarding_admin.flow_disabled") });
          fetchFlows();
        } else {
          toast({ variant: "error", title: json.error?.message || t("onboarding_admin.disable_error") });
        }
      } else {
        // Activate
        const res = await fetch(`${API_BASE}/v1/admin/onboarding/flows/${flow.id}/activate`, {
          method: "POST",
          headers: getHeaders(),
        });
        const json = await res.json();
        if (json.success) {
          toast({ variant: "success", title: t("onboarding_admin.flow_enabled") });
          fetchFlows();
        } else {
          toast({ variant: "error", title: json.error?.message || t("onboarding_admin.enable_error") });
        }
      }
    } catch {
      toast({ variant: "error", title: t("common.connection_error") });
    }
  };

  // ============ Editor Helpers ============

  const openNewFlow = () => {
    setEditingFlow(null);
    const initial = { nome: "", descricao: "", steps: [] as OnboardingStep[] };
    setFlowForm(initial);
    flowInitialRef.current = initial;
    setEditorOpen(true);
  };



  const openEditFlow = async (flow: OnboardingFlow) => {
    // Fetch full flow details
    try {
      const res = await fetch(`${API_BASE}/v1/admin/onboarding/flows/${flow.id}`, {
        headers: getHeaders(),
      });
      const json = await res.json();
      if (json.success && json.data) {
        const f = json.data as OnboardingFlow;
        setEditingFlow(f);
        const initial = { nome: f.nome, descricao: f.descricao || "", steps: f.steps || [] };
        setFlowForm(initial);
        flowInitialRef.current = initial;
        setEditorOpen(true);
      } else {
        toast({ variant: "error", title: t("onboarding_admin.save_error") });
      }
    } catch {
      toast({ variant: "error", title: t("common.connection_error") });
    }
  };

  // ============ Render ============

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-foreground">Onboarding</h1>
        <p className="text-muted-foreground text-sm mt-1">{t("onboarding_admin.description")}</p>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-border">
        {TABS.map((t) => (
          <button
            key={t.id}
            className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
              tab === t.id
                ? "border-primary-600 text-primary-600"
                : "border-transparent text-muted-foreground hover:text-foreground"
            }`}
            onClick={() => setTab(t.id)}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* ==================== FLUXOS TAB ==================== */}
      {tab === "fluxos" && (
        <div className="space-y-6">
          {/* Actions bar */}
          <div className="flex flex-wrap items-center gap-3">
            <Button onClick={openNewFlow}>
              <HeroIcon d="M12 4.5v15m7.5-7.5h-15" className="w-4 h-4 mr-1.5" />
              {t("onboarding_admin.new_flow")}
            </Button>
          </div>

          {/* Flows List */}
          {flowsLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
            </div>
          ) : flows.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <HeroIcon
                  d="M19.5 14.25v-2.625a3.375 3.375 0 0 0-3.375-3.375h-1.5A1.125 1.125 0 0 1 13.5 7.125v-1.5a3.375 3.375 0 0 0-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 0 0-9-9Z"
                  className="w-12 h-12 mx-auto text-muted-foreground mb-3"
                />
                <p className="text-muted-foreground">{t("onboarding_admin.empty")}</p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-5">
              {flows.map((flow) => (
                <Card key={flow.id}>
                  <CardContent className="p-5">
                    <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="font-semibold text-foreground truncate">{flow.nome}</h3>
                          <span
                            className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                              flow.isActive
                                ? "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400"
                                : "bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400"
                            }`}
                          >
                            {flow.isActive ? t("onboarding_admin.status_active") : t("onboarding_admin.status_inactive")}
                          </span>
                        </div>
                        {flow.descricao && (
                          <p className="text-sm text-muted-foreground line-clamp-2">{flow.descricao}</p>
                        )}
                        <div className="flex items-center gap-4 mt-1.5 text-xs text-muted-foreground">
                          <span>{flow.stepsCount} etapa{flow.stepsCount !== 1 ? "s" : ""}</span>
                          <span>Criado em {formatDate(flow.createdAt)}</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <button
                          className="px-3 py-1.5 text-sm rounded-md border border-border bg-card hover:bg-muted text-foreground transition-colors"
                          onClick={() => openEditFlow(flow)}
                        >
                          {t("common.edit")}
                        </button>
                        <button
                          className={`px-3 py-1.5 text-sm rounded-md border transition-colors ${
                            flow.isActive
                              ? "border-amber-300 text-amber-700 hover:bg-amber-50 dark:border-amber-700 dark:text-amber-400 dark:hover:bg-amber-900/20"
                              : "border-green-300 text-green-700 hover:bg-green-50 dark:border-green-700 dark:text-green-400 dark:hover:bg-green-900/20"
                          }`}
                          onClick={() => handleToggleActive(flow)}
                        >
                          {flow.isActive ? t("onboarding_admin.disable") : t("onboarding_admin.enable")}
                        </button>
                        <button
                          className="px-3 py-1.5 text-sm rounded-md border border-red-300 text-red-600 hover:bg-red-50 dark:border-red-700 dark:text-red-400 dark:hover:bg-red-900/20 transition-colors"
                          onClick={() => setDeleteTarget(flow)}
                        >
                          {t("onboarding_admin.delete")}
                        </button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ==================== LEADS TAB ==================== */}
      {tab === "leads" && (
        <div className="space-y-4">
          {/* Funnel Summary */}
          <div>
            <div className="flex items-center gap-3 mb-3">
              <span className="text-sm font-medium text-foreground">{t("onboarding_admin.funnel")}</span>
              <select
                className="rounded-md border border-border bg-card px-3 py-1.5 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary-500"
                value={funnelFlowId}
                onChange={(e) => setFunnelFlowId(e.target.value)}
              >
                {flows.map((f) => (
                  <option key={f.id} value={f.id}>{f.nome}{f.isActive ? " (ativo)" : ""}</option>
                ))}
              </select>
            </div>
            {funnel && (
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <Card>
                  <CardContent className="p-4 text-center">
                    <p className="text-3xl font-bold text-foreground">{funnel.totalStarted}</p>
                    <p className="text-sm text-muted-foreground mt-1">{t("onboarding_admin.started")}</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4 text-center">
                    <p className="text-3xl font-bold text-green-600 dark:text-green-400">{funnel.totalCompleted}</p>
                    <p className="text-sm text-muted-foreground mt-1">{t("onboarding_admin.completed")}</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4 text-center">
                    <p className="text-3xl font-bold text-primary-600">{funnel.conversionRate.toFixed(1)}%</p>
                    <p className="text-sm text-muted-foreground mt-1">{t("onboarding_admin.conversion")}</p>
                  </CardContent>
                </Card>
              </div>
            )}
          </div>

          {/* Filters */}
          <div className="flex flex-wrap items-center gap-3">
            <select
              className="rounded-md border border-border bg-card px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary-500"
              value={leadsStatusFilter}
              onChange={(e) => { setLeadsStatusFilter(e.target.value); setLeadsPage(1); }}
            >
              <option value="">{t("onboarding_admin.filter_all_status")}</option>
              <option value="in_progress">{t("onboarding_admin.filter_in_progress")}</option>
              <option value="completed">{t("onboarding_admin.filter_complete")}</option>
              <option value="abandoned">{t("onboarding_admin.filter_abandoned")}</option>
            </select>
            <select
              className="rounded-md border border-border bg-card px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary-500"
              value={leadsChannelFilter}
              onChange={(e) => { setLeadsChannelFilter(e.target.value); setLeadsPage(1); }}
            >
              <option value="">{t("onboarding_admin.filter_all_channels")}</option>
              <option value="app">{t("onboarding_admin.channel_app")}</option>
              <option value="whatsapp_bot">{t("onboarding_admin.channel_whatsapp")}</option>
            </select>
          </div>

          {/* Leads Table */}
          {leadsLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
            </div>
          ) : leads.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <p className="text-muted-foreground">{t("onboarding_admin.no_leads")}</p>
              </CardContent>
            </Card>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border">
                      <th className="text-left py-3 px-3 font-medium text-muted-foreground">CPF</th>
                      <th className="text-left py-3 px-3 font-medium text-muted-foreground">Nome</th>
                      <th className="text-left py-3 px-3 font-medium text-muted-foreground hidden sm:table-cell">WhatsApp</th>
                      <th className="text-left py-3 px-3 font-medium text-muted-foreground hidden md:table-cell">Canal</th>
                      <th className="text-left py-3 px-3 font-medium text-muted-foreground">Progresso</th>
                      <th className="text-left py-3 px-3 font-medium text-muted-foreground">Status</th>
                      <th className="text-left py-3 px-3 font-medium text-muted-foreground hidden lg:table-cell">Ultima atividade</th>
                    </tr>
                  </thead>
                  <tbody>
                    {leads.map((lead) => {
                      const badge = statusBadge(lead.status);
                      const progress = lead.totalSteps > 0 ? Math.round((lead.currentStep / lead.totalSteps) * 100) : 0;
                      return (
                        <tr key={lead.id} className="border-b border-border hover:bg-muted/50 transition-colors">
                          <td className="py-3 px-3 text-foreground font-mono text-xs">{lead.cpf || "-"}</td>
                          <td className="py-3 px-3 text-foreground">{lead.nome || "-"}</td>
                          <td className="py-3 px-3 text-foreground hidden sm:table-cell">{lead.whatsapp || "-"}</td>
                          <td className="py-3 px-3 text-foreground hidden md:table-cell">{channelLabel(lead.channel)}</td>
                          <td className="py-3 px-3">
                            <div className="flex items-center gap-2">
                              <div className="w-16 h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                                <div
                                  className="h-full bg-primary-600 rounded-full transition-all"
                                  style={{ width: `${progress}%` }}
                                />
                              </div>
                              <span className="text-xs text-muted-foreground whitespace-nowrap">
                                {lead.currentStep}/{lead.totalSteps}
                              </span>
                            </div>
                          </td>
                          <td className="py-3 px-3">
                            <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${badge.bg} ${badge.text}`}>
                              {badge.label}
                            </span>
                          </td>
                          <td className="py-3 px-3 text-muted-foreground text-xs hidden lg:table-cell">
                            {formatDate(lead.lastActivityAt)}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {leadsMeta.totalPages > 1 && (
                <div className="flex items-center justify-between pt-2">
                  <p className="text-sm text-muted-foreground">
                    {leadsMeta.total} lead{leadsMeta.total !== 1 ? "s" : ""} encontrado{leadsMeta.total !== 1 ? "s" : ""}
                  </p>
                  <div className="flex items-center gap-2">
                    <button
                      disabled={leadsPage <= 1}
                      className="px-3 py-1.5 text-sm rounded-md border border-border bg-card text-foreground hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      onClick={() => setLeadsPage((p) => Math.max(1, p - 1))}
                    >
                      Anterior
                    </button>
                    <span className="text-sm text-muted-foreground">
                      {leadsPage} / {leadsMeta.totalPages}
                    </span>
                    <button
                      disabled={leadsPage >= leadsMeta.totalPages}
                      className="px-3 py-1.5 text-sm rounded-md border border-border bg-card text-foreground hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      onClick={() => setLeadsPage((p) => Math.min(leadsMeta.totalPages, p + 1))}
                    >
                      Proximo
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* ==================== FLOW EDITOR DIALOG ==================== */}
      <Dialog
        open={editorOpen}
        onOpenChange={setEditorOpen}
        dirty={
          !!flowInitialRef.current &&
          JSON.stringify(flowForm) !== JSON.stringify(flowInitialRef.current)
        }
        onSave={async () => { await handleSaveFlow(); }}
      >
        <DialogContent className="max-w-[95vw] w-full max-h-[95vh] h-[90vh] overflow-hidden p-0 flex flex-col">
          <DialogHeader className="px-6 py-4 border-b border-border">
            <DialogTitle>{editingFlow ? t("onboarding_admin.edit_dialog") : t("onboarding_admin.new_dialog")}</DialogTitle>
          </DialogHeader>

          {/* Nome + Descricao bar */}
          <div className="flex items-center gap-4 px-6 py-3 border-b border-border bg-muted/30">
            <div className="flex-1">
              <input
                className="w-full rounded-md border border-border bg-card px-3 py-1.5 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary-500"
                value={flowForm.nome}
                onChange={(e) => setFlowForm({ ...flowForm, nome: e.target.value })}
                placeholder={t("onboarding_admin.flow_name")}
              />
            </div>
            <div className="flex-1">
              <input
                className="w-full rounded-md border border-border bg-card px-3 py-1.5 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary-500"
                value={flowForm.descricao}
                onChange={(e) => setFlowForm({ ...flowForm, descricao: e.target.value })}
                placeholder={t("onboarding_admin.flow_description")}
              />
            </div>
          </div>

          {/* Flow Editor Canvas */}
          <div className="flex-1 overflow-hidden min-h-0">
            <FlowEditor
              steps={flowForm.steps}
              onChange={(steps: OnboardingStep[]) => setFlowForm({ ...flowForm, steps })}
            />
          </div>

          <DialogFooter className="px-6 py-3 border-t border-border">
            <Button
              variant="outline"
              onClick={() => { setEditorOpen(false); setEditingFlow(null); }}
            >
              {t("common.cancel")}
            </Button>
            <Button onClick={handleSaveFlow} disabled={saving}>
              {saving ? t("common.saving") : editingFlow ? t("onboarding_admin.save_changes") : t("onboarding_admin.create_flow")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ==================== DELETE CONFIRM ==================== */}
      <ConfirmDialog
        open={!!deleteTarget}
        onOpenChange={(open) => { if (!open) setDeleteTarget(null); }}
        title={t("onboarding_admin.delete_title")}
        description={t("onboarding_admin.delete_desc", { nome: deleteTarget?.nome ?? "" })}
        confirmLabel={t("onboarding_admin.delete")}
        cancelLabel={t("common.cancel")}
        variant="danger"
        onConfirm={handleDeleteFlow}
      />
    </div>
  );
}
